import React from 'react';
import { useHistory } from 'react-router-dom';
import { withBlockExtensions } from '@plone/volto/helpers';
import { isInternalURL, flattenToAppURL } from '@plone/volto/helpers/Url/Url';
import {
  MPFCombobox,
  MPFLink,
  type MPFComboboxOption,
} from 'volto-ministerio-publico-federal/components/UI';
import { useServicos } from 'volto-ministerio-publico-federal/hooks/useServicos';
import {
  filtrarSugestoes,
  indiceDeBusca,
  trechoDestacado,
  type ServicosSugestao,
} from 'volto-ministerio-publico-federal/helpers/Servicos';

import {
  BUSCA_PADRAO,
  SUGESTOES_LIMITE,
  avisoMaisProcurados,
  mensagemSemResultados,
  textoDoStatus,
  toAtalhos,
  type ServicosBuscaData,
} from './helpers';

interface ViewProps {
  data: ServicosBuscaData;
  isEditMode?: boolean;
}

const MINIMO = 2;

function irParaAncora(href: string) {
  const alvo = document.getElementById(href.slice(1));
  if (!alvo) return;
  alvo.scrollIntoView({ behavior: 'smooth', block: 'start' });
  if (!alvo.hasAttribute('tabindex')) alvo.setAttribute('tabindex', '-1');
  alvo.focus({ preventScroll: true });
}

const Destaque: React.FC<{ titulo: string; termo: string }> = ({
  titulo,
  termo,
}) => {
  const { antes, trecho, depois } = trechoDestacado(titulo, termo);
  return (
    <>
      {antes}
      {trecho && <mark className="mpf-combobox__marca">{trecho}</mark>}
      {depois}
    </>
  );
};

export const MpfServicosBuscaBlockView: React.FC<ViewProps> = ({
  data,
  isEditMode,
}) => {
  const history = useHistory();
  const tituloId = React.useId();
  const atalhosId = React.useId();
  const [termo, setTermo] = React.useState('');
  const [todos, setTodos] = React.useState(false);
  const { categorias } = useServicos(data?.pasta?.[0]?.['@id']);

  const indice = React.useMemo(() => indiceDeBusca(categorias), [categorias]);
  const encontrados = React.useMemo(
    () => filtrarSugestoes(indice, termo, { minimo: MINIMO, limite: Infinity }),
    [indice, termo],
  );
  const sugestoes = React.useMemo(
    () => (todos ? encontrados : encontrados.slice(0, SUGESTOES_LIMITE)),
    [encontrados, todos],
  );
  const porId = React.useMemo(
    () => new Map(sugestoes.map((s) => [s.id, s])),
    [sugestoes],
  );

  const options: MPFComboboxOption[] = sugestoes.map((s) => ({
    id: s.id,
    content: <Destaque titulo={s.titulo} termo={termo} />,
    meta:
      s.tipo === 'categoria' ? 'Categoria de serviços' : `em ${s.categoria}`,
  }));

  const navegar = (href: string, externo = false) => {
    if (isEditMode || !href) return;
    if (href.startsWith('#')) {
      irParaAncora(href);
    } else if (!externo && isInternalURL(href)) {
      history.push(flattenToAppURL(href));
    } else {
      window.open(href, '_blank', 'noopener,noreferrer');
    }
  };

  const escolher = (option: MPFComboboxOption) => {
    const sugestao = porId.get(option.id) as ServicosSugestao | undefined;
    if (sugestao) navegar(sugestao.href, sugestao.externo);
  };

  const mudarTermo = (valor: string) => {
    setTermo(valor);
    setTodos(false);
  };

  const buscar = (valor: string) => {
    if (valor.trim()) setTodos(true);
  };

  const titulo = data?.titulo?.trim() || BUSCA_PADRAO.titulo;
  const Titulo = data?.tituloPrincipal === false ? 'h2' : 'h1';
  const atalhos = toAtalhos(data?.maisProcurados);
  const termoValido = termo.trim().length >= MINIMO;
  const semResultados = mensagemSemResultados(termo);
  const avisoAtalhos = isEditMode ? avisoMaisProcurados(atalhos.length) : null;

  return (
    <section
      className="mpf-servicos-busca not-prose"
      aria-labelledby={tituloId}
    >
      <div className="mpf-servicos-busca__cabecalho">
        <p className="mpf-servicos-busca__sobretitulo">
          {data?.sobretitulo ?? BUSCA_PADRAO.sobretitulo}
        </p>
        <Titulo id={tituloId} className="mpf-servicos-busca__titulo">
          {titulo}
        </Titulo>
        <p className="mpf-servicos-busca__texto">
          {data?.texto ?? BUSCA_PADRAO.texto}
        </p>
      </div>

      <form
        role="search"
        className="mpf-servicos-busca__form"
        onSubmit={(event) => {
          event.preventDefault();
          buscar(termo);
        }}
      >
        <MPFCombobox
          label="Buscar serviço"
          hideLabel
          placeholder={BUSCA_PADRAO.placeholder}
          value={termo}
          onValueChange={mudarTermo}
          options={options}
          onSelect={escolher}
          onSubmit={buscar}
          statusText={textoDoStatus(
            sugestoes.length,
            termoValido,
            semResultados,
          )}
          footer={
            termoValido && encontrados.length > sugestoes.length ? (
              <button
                type="button"
                className="mpf-combobox__ver-todos"
                onMouseDown={(event) => event.preventDefault()}
                onClick={() => setTodos(true)}
              >
                Ver todos os resultados
                <span className="mpf-servicos-busca__ver-todos-termo">
                  {' '}
                  para “{termo.trim()}”
                </span>
              </button>
            ) : null
          }
          emptyContent={
            termoValido ? (
              <p className="mpf-servicos-busca__vazio">{semResultados}</p>
            ) : null
          }
        />
      </form>

      {atalhos.length > 0 && (
        <div className="mpf-servicos-busca__atalhos">
          <span id={atalhosId} className="mpf-servicos-busca__atalhos-rotulo">
            {data?.maisProcuradosRotulo ?? BUSCA_PADRAO.maisProcuradosRotulo}
          </span>
          <ul
            className="mpf-servicos-busca__atalhos-lista"
            aria-labelledby={atalhosId}
          >
            {atalhos.map((atalho) => (
              <li key={atalho.key}>
                <MPFLink
                  href={atalho.href}
                  isEditMode={isEditMode}
                  openInNewTab={atalho.externo || undefined}
                  className="mpf-servicos-busca__chip"
                >
                  {atalho.titulo}
                </MPFLink>
              </li>
            ))}
          </ul>
        </div>
      )}

      {avisoAtalhos && (
        <p className="mpf-servicos-busca__aviso">{avisoAtalhos}</p>
      )}

      {isEditMode && !data?.pasta?.[0] && (
        <p className="mpf-servicos-busca__aviso">
          Escolha a Pasta de Serviços na barra lateral para as sugestões
          aparecerem.
        </p>
      )}
    </section>
  );
};

export default withBlockExtensions(MpfServicosBuscaBlockView);
